import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import {
  Activity, ArrowRight, ArrowLeft, Building2, CheckCircle2, ChevronLeft, ChevronRight, Clock, Coffee, Heart,
  HeartPulse, Home as HomeIcon, MapPin, Phone, ShieldCheck, ShoppingBag, Sparkles, Stethoscope, Sunrise, Trees, Users,
} from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLine, Reveal, SectionHeading } from "@/components/Reveal";
import { TiltCard } from "@/components/TiltCard";
import { Marquee } from "@/components/Marquee";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { HOME_SERVICES, IMAGES, MANIFESTO, MARQUEE_ITEMS, PRICING, STEPS, TESTIMONIALS, WHO_WE_HELP, WHY_BRIGHTSIDE } from "@/data/content";

const ICONS = { Coffee, Trees, Stethoscope, Phone, Sparkles, Heart, ShoppingBag, HeartPulse, Building2 };

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Brightside Befriending Services Ltd",
  description: "Private companionship and befriending visits for older adults across Glasgow and surrounding towns, Ayr and South Ayrshire. All befrienders PVG checked.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "2/3, 2nd Floor, 48 West George Street",
    addressLocality: "Glasgow",
    postalCode: "G2 1BP",
    addressCountry: "GB",
  },
  areaServed: ["Glasgow", "East Renfrewshire", "East Dunbartonshire", "Lanarkshire", "Ayr", "South Ayrshire"],
};

const Hero = () => {
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 600], [0, 70]);

  return (
    <section className="relative overflow-hidden pb-16 pt-32 sm:pt-36 lg:pb-24 lg:pt-44" data-testid="home-hero">
      <div className="pointer-events-none absolute -left-40 top-20 h-[28rem] w-[28rem] rounded-full bg-sage-light/60 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[24rem] w-[24rem] rounded-full bg-terracotta-soft/40 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage-tint px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-forest" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />
              Befriending & companionship · Glasgow & Ayr
            </p>
          </Reveal>
          <h1 className="mt-7 font-serif text-4xl tracking-tight text-forest sm:text-5xl lg:text-6xl lg:leading-[1.06]">
            <MaskedLine delay={0.1}>A friendly face,</MaskedLine>
            <MaskedLine delay={0.22} className="italic text-terracotta">a listening ear,</MaskedLine>
            <MaskedLine delay={0.34}>right on your doorstep.</MaskedLine>
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-pebble sm:text-lg" data-testid="hero-subtext">
              Brightside introduces carefully matched, PVG-checked befrienders to older people across Glasgow — for conversation, walks, appointments and everything in between.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button
                asChild
                data-testid="hero-book-button"
                className="group rounded-full bg-forest px-8 py-6 text-base text-linen shadow-[0_14px_30px_rgba(28,56,41,0.28)] transition-colors duration-300 hover:bg-terracotta"
              >
                <Link to="/contact">
                  Book a free consultation
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                data-testid="hero-services-button"
                className="rounded-full px-6 py-6 text-base text-forest underline-offset-4 hover:bg-transparent hover:text-terracotta hover:underline"
              >
                <Link to="/services">See our services</Link>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={0.75}>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium text-forest/70">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sage" /> 100% PVG checked</span>
              <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-sage" /> Glasgow, Ayr & surrounds</span>
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-sage" /> No waiting lists</span>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}>
            <TiltCard className="relative" data-testid="hero-image-card">
              <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-[radial-gradient(closest-side,rgba(200,109,81,0.22),transparent)] blur-xl" />
              <div className="photo-frame relative overflow-hidden rounded-[2.5rem] shadow-[0_30px_60px_rgba(28,56,41,0.22)]">
                <motion.img
                  src={IMAGES.hero}
                  alt="Two older women sharing tea and conversation at a sunny table"
                  className="h-[26rem] w-full object-cover sm:h-[30rem]"
                  style={{ y: yImage, scale: 1.12 }}
                />
              </div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-4 top-8 rounded-2xl border border-sand bg-white/90 px-4 py-3 shadow-lg backdrop-blur-md sm:-left-8"
                data-testid="hero-chip-pvg"
              >
                <p className="flex items-center gap-2 text-xs font-semibold text-forest"><ShieldCheck className="h-4 w-4 text-sage" /> Every befriender PVG checked</p>
              </motion.div>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -right-3 bottom-10 rounded-2xl border border-sand bg-white/90 px-4 py-3 shadow-lg backdrop-blur-md sm:-right-6"
                data-testid="hero-chip-price"
              >
                <p className="text-[11px] font-medium uppercase tracking-widest text-pebble">Always starts with</p>
                <p className="font-serif text-2xl font-semibold text-terracotta">A free chat</p>
              </motion.div>
            </TiltCard>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const TrustStrip = () => (
  <section className="border-y border-sand bg-white/60" data-testid="trust-strip">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:px-8">
      {[
        { icon: ShieldCheck, title: "PVG checked & vetted", text: "Every befriender, without exception" },
        { icon: Clock, title: "Flexible & fast to start", text: "Visits usually begin within days" },
        { icon: MapPin, title: "Glasgow, Ayr & beyond", text: "Glasgow & surrounding towns, plus Ayr & South Ayrshire" },
        { icon: Phone, title: "Calls between visits", text: "Telephone & video check-ins available" },
      ].map((item, i) => (
        <Reveal key={item.title} delay={i * 0.08} className="flex items-start gap-3.5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sage-tint text-sage">
            <item.icon className="h-5 w-5" />
          </span>
          <span>
            <span className="block text-sm font-semibold text-forest">{item.title}</span>
            <span className="mt-0.5 block text-xs leading-relaxed text-pebble">{item.text}</span>
          </span>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="manifesto-section">
    <div className="mx-auto max-w-7xl">
      <SectionHeading
        eyebrow="Why Brightside exists"
        title="A small company with a simple belief: nobody in Glasgow should feel invisible."
      />
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {MANIFESTO.map((chapter, i) => (
          <Reveal key={chapter.num} delay={i * 0.08}>
            <article
              data-testid={`manifesto-chapter-${chapter.num}`}
              className="group relative h-full overflow-hidden rounded-3xl border border-sand bg-linen-deep p-8 transition-shadow duration-500 hover:shadow-[0_20px_45px_rgba(28,56,41,0.1)] sm:p-10"
            >
              <span className="pointer-events-none absolute -right-3 -top-7 font-serif text-[7rem] font-semibold leading-none text-terracotta/15 transition-colors duration-500 group-hover:text-terracotta/25">
                {chapter.num}
              </span>
              <h3 className="relative font-serif text-xl text-forest sm:text-2xl">{chapter.title}</h3>
              <p className="relative mt-4 max-w-md text-sm leading-relaxed text-pebble sm:text-base">{chapter.text}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WHO_ICONS = { Users, Heart, Sunrise, HomeIcon, Activity, Coffee };

const WhoWeHelp = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="who-we-help">
    <div className="mx-auto max-w-7xl">
      <SectionHeading
        eyebrow="Who our service is for"
        title="Made for the people who make Glasgow"
        text="No assessments, no eligibility criteria, no waiting lists — if someone you love would benefit from company, they're exactly who we're here for."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {WHO_WE_HELP.map((item, i) => {
          const Icon = WHO_ICONS[item.icon];
          return (
            <Reveal key={item.title} delay={(i % 3) * 0.08}>
              <article
                data-testid={`who-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group h-full rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-terracotta/40 hover:shadow-[0_24px_50px_rgba(28,56,41,0.12)]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta/10 text-terracotta transition-colors duration-500 group-hover:bg-terracotta group-hover:text-linen">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-serif text-xl text-forest">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-pebble">{item.text}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const ServicesShowcase = () => (
  <section className="bg-white/50 px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="services-showcase">
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <SectionHeading
          eyebrow="What we do"
          title="Companionship, in all its ordinary magic"
          text="From a blether over tea to an arm to lean on at the hospital — every visit is shaped around the person, never a checklist."
        />
        <Reveal delay={0.2}>
          <Button asChild variant="outline" data-testid="showcase-all-services-button" className="group w-fit rounded-full border-forest/30 px-6 text-forest hover:bg-forest hover:text-linen">
            <Link to="/services">
              Explore our services
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {HOME_SERVICES.map((service, i) => {
          const Icon = ICONS[service.icon];
          return (
            <Reveal key={service.title} delay={(i % 3) * 0.08}>
              <article
                data-testid={`service-card-${service.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group h-full rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-sage/40 hover:shadow-[0_24px_50px_rgba(28,56,41,0.12)]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-tint text-sage transition-colors duration-500 group-hover:bg-terracotta group-hover:text-linen">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-serif text-xl text-forest">{service.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-pebble">{service.text}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const HowTeaser = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="how-teaser">
    <div className="mx-auto max-w-7xl">
      <SectionHeading eyebrow="How it works" title="From first hello to familiar face, in four gentle steps" />
      <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step, i) => (
          <Reveal key={step.num} delay={i * 0.1} className="relative">
            <span className="font-serif text-5xl font-semibold text-terracotta/30">{step.num}</span>
            <h3 className="mt-3 font-serif text-xl text-forest">{step.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-pebble">{step.text}</p>
            {i < 3 && <span className="absolute -right-5 top-6 hidden h-px w-6 bg-sand lg:block" />}
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.3}>
        <Button asChild variant="ghost" data-testid="how-teaser-link" className="group mt-10 rounded-full px-2 text-forest hover:bg-transparent hover:text-terracotta">
          <Link to="/how-it-works">
            See the full journey
            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Button>
      </Reveal>
    </div>
  </section>
);

const TestimonialSlider = () => {
  const [index, setIndex] = useState(0);
  const t = TESTIMONIALS[index];
  const go = (dir) => setIndex((i) => (i + dir + TESTIMONIALS.length) % TESTIMONIALS.length);

  return (
    <section className="bg-forest px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="testimonial-slider">
      <div className="mx-auto max-w-4xl text-center">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-terracotta-soft">Kind words</p>
        </Reveal>
        <div className="relative mt-10 min-h-[16rem] sm:min-h-[13rem]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="font-serif text-2xl italic leading-relaxed text-linen sm:text-3xl">“{t.quote}”</p>
              <footer className="mt-7 text-sm text-linen/70">
                <span className="font-semibold text-terracotta-soft">{t.name}</span> · {t.relation} · {t.area}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="mt-10 flex items-center justify-center gap-4">
          <button data-testid="testimonial-prev-button" onClick={() => go(-1)} aria-label="Previous story" className="flex h-11 w-11 items-center justify-center rounded-full border border-linen/25 text-linen/80 transition-colors hover:border-terracotta hover:bg-terracotta hover:text-linen">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to story ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-7 bg-terracotta" : "w-2 bg-linen/30 hover:bg-linen/60"}`}
              />
            ))}
          </div>
          <button data-testid="testimonial-next-button" onClick={() => go(1)} aria-label="Next story" className="flex h-11 w-11 items-center justify-center rounded-full border border-linen/25 text-linen/80 transition-colors hover:border-terracotta hover:bg-terracotta hover:text-linen">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-8 text-xs text-linen/40">Illustrative stories — real family testimonials will live here as Brightside grows.</p>
      </div>
    </section>
  );
};

const WhyUs = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="why-brightside">
    <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
      <div>
        <SectionHeading
          eyebrow="Why families choose us"
          title="Private befriending, without the waiting list"
          text="Council and charity services do wonderful work — but they're stretched. Brightside is the flexible, personal alternative for families who'd rather not wait."
        />
        <div className="mt-10 space-y-6">
          {WHY_BRIGHTSIDE.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.08} className="flex gap-4">
              <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-terracotta" />
              <div>
                <h3 className="font-semibold text-forest">{item.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-pebble">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="photo-frame relative overflow-hidden rounded-[2.5rem] shadow-[0_30px_60px_rgba(28,56,41,0.18)]">
          <img src={IMAGES.walk} alt="An older person and companion walking together through a sunny park" className="h-[30rem] w-full object-cover" />
          <div className="absolute bottom-5 left-5 rounded-2xl border border-white/40 bg-white/85 px-5 py-4 backdrop-blur-md">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-pebble">Every befriender</p>
            <p className="font-serif text-2xl font-semibold text-forest">PVG checked</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const PricingTeaser = () => {
  const picks = [PRICING[0], PRICING[5], PRICING[6]];
  return (
    <section className="bg-white/50 px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="pricing-teaser">
      <div className="mx-auto max-w-7xl">
        <SectionHeading eyebrow="Flexible arrangements" title="Simple arrangements, shaped around you" text="One-off visits, monthly bundles or something in between — everything is invoiced monthly in arrears, so you only ever pay for visits that have happened." />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {picks.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <article
                data-testid={`pricing-teaser-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative h-full rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1.5 ${
                  p.popular ? "bg-forest text-linen shadow-[0_24px_50px_rgba(28,56,41,0.3)]" : "border border-sand bg-white text-ink"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-8 rounded-full bg-terracotta px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-linen">Most loved</span>
                )}
                <h3 className={`font-serif text-xl ${p.popular ? "text-linen" : "text-forest"}`}>{p.title}</h3>
                <p className={`mt-4 text-sm leading-relaxed ${p.popular ? "text-linen/70" : "text-pebble"}`}>{p.description}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <Button asChild variant="ghost" data-testid="pricing-teaser-link" className="group mt-10 rounded-full px-2 text-forest hover:bg-transparent hover:text-terracotta">
            <Link to="/services">
              See every service
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Seo
        title="Brightside Befriending Services — Companionship & befriending for older people in Glasgow"
        siteName="Brightside Befriending Services"
        description="Private, PVG-checked befriending visits, appointment companions and telephone check-ins for older adults across Glasgow and surrounding towns, Ayr and South Ayrshire. Free consultation."
        jsonLd={jsonLd}
      />
      <Hero />
      <TrustStrip />
      <Marquee items={MARQUEE_ITEMS} />
      <Manifesto />
      <WhoWeHelp />
      <ServicesShowcase />
      <HowTeaser />
      <TestimonialSlider />
      <WhyUs />
      <PricingTeaser />
      <CTASection />
    </>
  );
}
